import React from "react";
import { ToggleButton } from "primereact/togglebutton";
import { useSetting, useSettingsActions } from "./SettingsStore.tsx";
import {
    showsAlignmentGrid,
    showsCarryBoxes,
    showsLargePrint,
    showsPresentation,
} from "./AdvancedSettings.ts";
import { useTranslation } from "../../Shared/i18n/useTranslation.ts";

/**
 * Presentation scaffolds (ADR 0014): carry boxes, an alignment grid, large
 * print. Each control is shown only where it means something (§2) - a carry box
 * above a clock face is meaningless - and hidden, not disabled, otherwise.
 *
 * The "where it means something" conditions live in AdvancedSettings.ts rather
 * than here, because the advanced-tier toggle has to ask the same questions to
 * know whether one of these is doing anything (ADR 0023 §6).
 */
export const PresentationSettings = React.memo(function PresentationSettings() {
    const { getSettings, setSettings } = useSettingsActions();
    const { presentation, showCarryBoxes, showAlignmentGrid, showLargePrint, showAny } = useSetting((settings) => ({
        presentation: settings.Presentation ?? {
            carryBoxes: false,
            alignmentGrid: false,
            largePrint: false,
        },
        showCarryBoxes: showsCarryBoxes(settings),
        showAlignmentGrid: showsAlignmentGrid(settings),
        showLargePrint: showsLargePrint(settings),
        showAny: showsPresentation(settings),
    }));

    function set(key: "carryBoxes" | "alignmentGrid" | "largePrint", value: boolean) {
        const settings = getSettings();
        const current = settings.Presentation ?? presentation;
        setSettings({ ...settings, Presentation: { ...current, [key]: value } });
    }

    const t = useTranslation();

    if (!showAny) {
        return null;
    }

    return (
        <div className="presentationSettings">
            <span className="settingLabel">{t('settings.layoutHelpLabel')}</span>
            {showCarryBoxes && (
                <div className="answerPagesContainer">
                    <label htmlFor="carryBoxes">{t('settings.carryBoxesLabel')}</label>
                    <ToggleButton onLabel={t('settings.toggleYes')} offLabel={t('settings.toggleNo')}
                        inputId="carryBoxes"
                        className="answerPagesButton"
                        checked={presentation.carryBoxes}
                        onChange={(e) => set("carryBoxes", e.value)}
                    />
                </div>
            )}
            {showAlignmentGrid && (
                <div className="answerPagesContainer">
                    <label htmlFor="alignmentGrid">{t('settings.alignmentGridLabel')}</label>
                    <ToggleButton onLabel={t('settings.toggleYes')} offLabel={t('settings.toggleNo')}
                        inputId="alignmentGrid"
                        className="answerPagesButton"
                        checked={presentation.alignmentGrid}
                        onChange={(e) => set("alignmentGrid", e.value)}
                    />
                </div>
            )}
            {showLargePrint && (
                <div className="answerPagesContainer">
                    <label htmlFor="largePrint">{t('settings.largePrintLabel')}</label>
                    <ToggleButton onLabel={t('settings.toggleYes')} offLabel={t('settings.toggleNo')}
                        inputId="largePrint"
                        className="answerPagesButton"
                        checked={presentation.largePrint}
                        onChange={(e) => set("largePrint", e.value)}
                    />
                </div>
            )}
        </div>
    );
});

export default PresentationSettings;
